<template>
    <div style="position:relative; overflow: hidden">
        <div id="map" class="map">
            <div class="map-controls">
                <!-- Componentes de controle do canto inferior esquerdo devem ser colocados dentro desta div -->
                <UseSoloPermissionLegend v-if="showCnaeLegend" />
                <GeneralMapSubtitle
                    description="Zonas"
                    :src="zonesLegendSrc"
                />
                <GeneralMapControls :map="mapRef" v-if="!!mapRef" @draw-start="() => isDrawing = true" @draw-end="() => isDrawing = false" />
            </div>

            <div>
                <MapLayerSwitcher :map="mapRef" v-if="!!mapRef" />
            </div>

            <div class="map-search">
                <!-- Componentes de pesquisa do canto superior esquerdo devem ser colocados dentro desta div -->
                <ViabilityMapControls @change-search-type="changeSearchType" @select-search-item="selectSearchItem" v-if="isAreaOfInterest.success" ref="controlsRef" />
            </div>

            <div class="map-popups" id="map-popups">
                <!-- Componentes de pop up (aparecem sobre o mapa sem posição fixa) devem ser colocados dentro desta div -->
                <div id="tooltip" class="map-tooltip"></div>
                <HomeContextMenu :map="mapRef" :disable-permission-check="true" v-if="!!mapRef" />
                <FeatureModal ref="modalRef" @hide="closePopover" v-if="isAreaOfInterest.success"/>
            </div>
        </div>
    </div>
</template>

<script setup>
import 'ol/ol.css'
import 'ol-ext/dist/ol-ext.css'
import {toLonLat, fromLonLat} from 'ol/proj'
import Map from 'ol/Map'
import View from 'ol/View'
import {baseLayers} from '../../../../open-layers/baseLayers'
import {scaleLine} from '../../../../open-layers/default-controls.js'
import FeatureModal from "./FeatureModal.vue";
import { Modal } from 'ant-design-vue'
import {
    addLocationIconToCoordinate, checkAreaOfInterest, getLayerByName,
    getMapFeaturesByLayer, removeLayerByName
} from '../../../../open-layers/helpers.js'

import {getCoordinateByPropertyId, getViabilityMapLayers, setupZoneamentoTooltip} from "../../../../open-layers/map-helpers/viability";

import {computed, onMounted, ref} from 'vue'
import ViabilityMapControls from "./ViabilityMapControls.vue";
import {extend as extendExtent, getCenter} from "ol/extent";
import Style from "ol/style/Style";
import Fill from "ol/style/Fill";
import Stroke from "ol/style/Stroke";
import UseSoloPermissionLegend from "./UseSoloPermissionLegend.vue";
import GeneralMapControls from "../Components/GeneralMapControls.vue";
import GeneralMapSubtitle from "../Components/GeneralMapSubtitle.vue";
import HomeContextMenu from "../Components/HomeContextMenu.vue";
import { getDefaultMapCenter, getDefaultMapZoom } from '../../../../open-layers/map-defaults.js';
import { geoServerLegendUrl } from '../../../../open-layers/geoserver.js';
import {defaults as defaultControls} from "ol/control/defaults";
import MapLayerSwitcher from "../Components/MapLayerSwitcher.vue";

const activePopoverCoordinate = ref(null)
const showCnaeLegend = ref(false)
const mapRef = ref(null)
const controlsRef = ref()
const zones = ref()
const modalRef = ref(null)
const isAreaOfInterest = ref({success: true})
const center = ref(getDefaultMapCenter())
const isDrawing = ref(false)

const zonesLegendSrc = geoServerLegendUrl('geo_viabilidade')

const transparentStyle = new Style({ fill: new Fill({ color: 'rgba(0, 0, 0, 0)' }) })
const allowedStyle = new Style({ fill: new Fill({ color: 'rgba(0, 128, 0, 0.7)' }) })
const permissibleStyle = new Style({ fill: new Fill({ color: 'rgba(255, 255, 0, 0.7)' }) })
const prohibitedStyle = new Style({ fill: new Fill({ color: 'rgba(255, 0, 0, 0.7)' }) })

const waitForLayerFeatures = async (layer) => {
    if (!layer) return []

    const source = layer.getSource?.()
    if (!source) return []

    if (layer.get('lazy_loaded') || source.getFeatures().length > 0) return source.getFeatures()

    await new Promise((resolve) => {
        const finish = () => {
            source.un('addfeature', onChange)
            source.un('change', onChange)
            clearTimeout(timeoutId)
            resolve()
        }

        const onChange = () => {
            if (layer.get('lazy_loaded') || source.getFeatures().length > 0) {
                finish()
            }
        }

        const timeoutId = setTimeout(finish, 4000)

        source.on('addfeature', onChange)
        source.on('change', onChange)
        onChange()
    })

    return source.getFeatures()
}

const changeSearchType = (searchType) => {
    const imageZone = getLayerByName(mapRef.value, 'Zonas')

    imageZone.setOpacity(0.75)

    let featuresZone = getMapFeaturesByLayer('Viabilidade', mapRef.value)
    featuresZone.forEach(ft => {
        ft.setStyle(transparentStyle)
    })

    let featuresStreet = getMapFeaturesByLayer('Ruas Vetor', mapRef.value)
    featuresStreet.forEach(ft => {
        ft.setStyle(transparentStyle)
    })

    showCnaeLegend.value = (searchType === 'cnae')
}

const selectSearchItem = (e) => {
    const { searchType, propertyId, streetId, cnaeId } = e

    if (searchType === 'propertyId' || searchType === 'sql') {
        return selectFeatureByPropertyId(propertyId)
    }

    if (searchType === 'address') {
        return propertyId
            ? selectFeatureByPropertyId(propertyId)
            : selectStreetById(streetId)
    }

    if (searchType === 'cnae') {
        return setPermissionFeatureStyleToZone(cnaeId)
    }
}

const closePopover = () => {
    removeLayerByName(mapRef.value, 'location-icon')
}
const selectFeatureByPropertyId = async (propertyId) => {
    const coordinate = await getCoordinateByPropertyId(propertyId)
    await (new Promise(resolve => setTimeout(resolve, 500)));
    openDetailsByCoordinate(coordinate, propertyId)
}

const setPermissionFeatureStyleToZone = async (cnaeId) => {
    const imageZone = getLayerByName(mapRef.value, 'Zonas')
    imageZone.setVisible(false)

    let layerViability = getLayerByName(mapRef.value, 'Viabilidade')
    layerViability.setVisible(true)

    const viabilityFeatures = await waitForLayerFeatures(layerViability)

    viabilityFeatures.forEach(ft => {
        ft.setStyle(prohibitedStyle)
    })

    const zones = (await window.axios.get(`/api/viabilidade/cnae/${cnaeId}/zones`)).data.data

    if (zones.length === 0) return

    zones.forEach(zn => {
        let featuresZone = viabilityFeatures
            .filter(ft => ft.get('zoneamento_id') === zn.zoneamento_id)

        featuresZone.forEach(ft => {
            switch (zn.type_permission) {
                case 'allowed':
                    ft.setStyle(allowedStyle)
                    break
                case 'permissible':
                    ft.setStyle(permissibleStyle)
                    break
                case 'prohibited':
                    ft.setStyle(prohibitedStyle)
                    break
            }
        })
    })
}

const selectStreetById = async (streetId) => {
    const streetLayer = getLayerByName(mapRef.value, 'Ruas')

    streetLayer?.setVisible(true)

    const style = new  Style({
        stroke: new Stroke({ color: 'yellow', width: 7 }),
    });
    const normalizedStreetId = String(streetId)
    const features = (await getMapFeaturesByLayer('Ruas Vetor', mapRef.value)) ?? []

    const feature = features.filter(f => {
        if (f.get('selected')) {
            f.setStyle(new Style ({
                fill: new Fill({color: 'transparent'})
            }))
        }
        const featureStreetId = f.get('codigo_ecidade') ?? f.get('street_id') ?? f.get('j14_codigo')
        return featureStreetId != null && String(featureStreetId) === normalizedStreetId
    });
    if (!feature.length) return

    feature.map(ft => {
        ft.set('selected', true)
        ft.setStyle(style)
    })

    const view = mapRef.value.getView();
    const extent = feature.slice(1).reduce((accumulator, currentFeature) => {
        return extendExtent(accumulator, currentFeature.getGeometry().getExtent())
    }, feature[0].getGeometry().getExtent().slice())

    view.fit(extent, {
        padding: [200, 200, 200, 200],
        duration: 600,
    });
}

const getZones = async () => {
    zones.value = (await window.axios.get('/api/viabilidade/zoneamentos')).data.data
}
const isLonLat = ([x, y]) =>
    Math.abs(x) <= 180 && Math.abs(y) <= 90; // heurística simples

const toViewProj = (map, coord) => {
    const viewProj = map.getView().getProjection();
    return isLonLat(coord) ? fromLonLat(coord, viewProj) : coord;
};

const openDetailsByCoordinate = (coordinate, propertyId = null) => {
    if (isDrawing.value) return
    const map = mapRef.value;
    const viewProj = map.getView().getProjection();

    // Normalize para a projeção do mapa
    const coordInView = toViewProj(map, coordinate);

    map.getView().animate({
        center: coordInView,
        zoom: 17,
        duration: 1000
    }, (completed) => {
        if (!completed) return
        map.once('rendercomplete', () => renderMap(map, coordInView, viewProj, propertyId))
    });
};

function renderMap(map, coordInView, viewProj, propertyId) {
    // Use SEMPRE a coordenada na projeção do mapa para pixel/ícone
    activePopoverCoordinate.value = coordInView;
    addLocationIconToCoordinate(map, coordInView);

    // Lon/lat só para exibir no modal
    const [lon, lat] = toLonLat(coordInView, viewProj);

    const pixel = map.getPixelFromCoordinate(coordInView);

    const clickedFeature = map.forEachFeatureAtPixel(
        pixel,
        (feature) => feature,
        {
            layerFilter: (layer) => layer.get('name') === 'Viabilidade',
            hitTolerance: 6, // ajuda a “pegar” o clique
        }
    );

    if (clickedFeature) {
        const zoneId = clickedFeature.get('zoneamento_id');
        const zone = zones.value.find(zn => zn.id === zoneId);

        const props = {
            id: zone?.id,
            acronym: zone?.description,
            name: zone?.objective,
            latitude: lat,
            longitude: lon,
            cnaes: controlsRef.value.cnaeSuggestions,
            ...(propertyId ? { propertyId } : {})
        };

        modalRef.value.openPopover(props, pixel);
    } else {
        modalRef.value.closePopover();
        activePopoverCoordinate.value = null;
    }
};
function confirmModal({ title, content }) {
    return new Promise((resolve) => {
        Modal.confirm({
            title,
            content,
            okText: 'Ok',
            cancelText: 'Cancelar',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
        })
    })
}

onMounted(async () => {
    await getZones()
    
    isAreaOfInterest.value = await checkAreaOfInterest()
    if (!isAreaOfInterest.value.success) {
        await confirmModal({
            title: 'Verificação de Localização',
            content: '❌ Você está fora da área do município!'
        })

        center.value = isAreaOfInterest.value.coords
    }

    const map = new Map({
        target: 'map',
        layers: [baseLayers()],
        view: new View({
            center: center.value,
            zoom: getDefaultMapZoom(),
        }),
        controls: defaultControls({ rotate: false, zoom: false }).extend([scaleLine])
    })

    if (!isAreaOfInterest.value.success) {
        addLocationIconToCoordinate(map, center.value)
    }

    const viabilityLayers = await getViabilityMapLayers()
    viabilityLayers.forEach((layer) => {
        if (layer) map.addLayer(layer)
    })

    map.on('click', async (evt) => {
        openDetailsByCoordinate(evt.coordinate)
    })


    setupZoneamentoTooltip(map, {
        zones,
        tooltipElement: document.getElementById('tooltip'),
        activePopoverCoordinateRef: activePopoverCoordinate,
        modalRef,
        onZoneChange: (zone) => {}
    })

    mapRef.value = map
});

</script>

<style scoped>
.map {
    width: 100%;
    height: 100vh;
    z-index: 0;
    position: relative;
}

.map-tooltip {
    position: absolute;
    background: rgba(0, 0, 0, 0.75);
    color: white;
    padding: 6px 10px;
    font-size: 12px;
    border-radius: 4px;
    pointer-events: none;
    white-space: nowrap;
    transform: translate(10px, -20px);
    z-index: 1000;
    opacity: 0;
}

::v-deep(.ol-zoom) {
    position: absolute;
    right: 10px;
    left: auto !important;
    z-index: 1000;
}

::v-deep(.floating-modal.ant-modal-wrap) {
    pointer-events: none;
}

::v-deep(.floating-modal .ant-modal) {
    pointer-events: auto;
}

::v-deep(.ol-layerswitcher) {
    margin-top: 10px;
}

.info-button {
    color: blue;
    font-weight: bold;
}

.alert-button {
    color: red;
    font-weight: bold;
}

.ol-popup {
    position: absolute;
    background: white;
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 6px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
    min-width: 150px;
    font-size: 14px;
}

.legend-wrapper > * {
    position: static;
}

.map-search {
    position: absolute;
    gap: 8px;
    top: calc(
        8px +
        var(--home-map-icon-size, 32px) +
        max(8px, calc(var(--home-map-icon-size, 32px) * 0.25))
    );
    left: 8px;
    z-index: 1000;
}

.map-controls {
    position: absolute;
    display: flex;
    flex-direction: column;
    gap: 8px;
    bottom: 20px;
    left: 8px;
    z-index: 1000;
}
</style>
